import { ExportButton } from '@/components/exports/ExportButton';
import { FilterPanel } from '@/components/ui/filter-panel';
import type { CommissionHistoryFilters as CommissionHistoryQueryFilters } from '@/hooks/queries/use-commission-history-query';
import { useCommissionHistoryQuery } from '@/hooks/queries/use-commission-history-query';
import { VStack } from '@chakra-ui/react';
import type { CommissionStatus } from '@fluenceur/types';
import { DownloadDataWindow } from 'iconoir-react';

import { parseAsString, useQueryState } from 'nuqs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommissionHistoryFilters } from './CommissionHistoryFilters';
import { CommissionHistoryTable } from './CommissionHistoryTable';

export const CommissionHistoryView: React.FC = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Read filter values from URL using nuqs
  const [status] = useQueryState('status', parseAsString);
  const [affiliateIds] = useQueryState('affiliateIds', parseAsString);
  const [startDate] = useQueryState('startDate', parseAsString);
  const [endDate] = useQueryState('endDate', parseAsString);

  // Build filters object from URL state
  const historyFilters: CommissionHistoryQueryFilters = {};
  if (status) historyFilters.status = status as CommissionStatus;
  if (affiliateIds) historyFilters.affiliateIds = affiliateIds.split(',').filter(Boolean);
  if (startDate) historyFilters.startDate = startDate;
  if (endDate) historyFilters.endDate = endDate;

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [status, affiliateIds, startDate, endDate]);

  // Fetch history data
  const { data: historyData, isLoading: isHistoryLoading } = useCommissionHistoryQuery({
    ...historyFilters,
    page: currentPage,
    limit: 10,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <VStack gap={5} w="full" align="stretch">
      <FilterPanel
        w="full"
        bodyProps={{ p: 3 }}
        actionsSlot={
          <ExportButton
            resource="commissions"
            filters={historyFilters as Record<string, unknown>}
            label={t('commissions.history.export')}
            leftIcon={<DownloadDataWindow width={16} height={16} />}
            size="sm"
            variant="outline"
            colorPalette="gray"
          />
        }
      >
        <CommissionHistoryFilters />
      </FilterPanel>
      <CommissionHistoryTable
        commissions={Array.isArray(historyData?.data) ? historyData.data : []}
        pagination={historyData?.meta}
        loading={isHistoryLoading}
        onPageChange={handlePageChange}
      />
    </VStack>
  );
};
